import {
	ProfessorInternshipApi,
	ProfessorInternshipAddUpdateDTO,
	InternshipEngagementType,
	InternshipSortFieldEnum,
	SortDirectionEnum,
	InternshipStatus,
	InternshipDTOPagedResponseFromJSON,
} from "../Api";
import { BASE_PATH } from "../Api/runtime";
import { arrayParamAjust } from "../utils/dataUtils";
import { getAuthConfiguration } from "./configuration";

type ProfessorInternshipQueryParamValue =
	| string
	| number
	| boolean
	| string[]
	| number[]
	| boolean[];

const professorInternshipGet = async (
	action: string,
	queryParams: Record<string, ProfessorInternshipQueryParamValue>
) => {
	const token = localStorage.getItem("token");
	const searchParams = new URLSearchParams();

	Object.entries(queryParams).forEach(([key, value]) => {
		if (value === undefined || value === null) return;
		if (Array.isArray(value)) {
			value.forEach((item) => searchParams.append(key, String(item)));
		} else {
			searchParams.append(key, String(value));
		}
	});

	const query = searchParams.toString();
	const response = await fetch(
		`${BASE_PATH}/api/ProfessorInternship/${action}${query ? `?${query}` : ""}`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	);

	if (!response.ok) {
		throw new Error(await response.text());
	}

	return response.json();
};

const AuthProfessorInternshipApiFactory = () =>
	new ProfessorInternshipApi(getAuthConfiguration());

export const addProfessorInternship = async (
	professorInternshipAddUpdateDTO: ProfessorInternshipAddUpdateDTO
) => {
	await AuthProfessorInternshipApiFactory().apiProfessorInternshipAddPost({
		professorInternshipAddUpdateDTO,
	});
};

export const deleteProfessorInternship = async (id: string) => {
	await AuthProfessorInternshipApiFactory().apiProfessorInternshipDeleteIdDelete(
		{ id }
	);
};

export const duplicateProfessorInternship = async (id: string) => {
	await AuthProfessorInternshipApiFactory().apiProfessorInternshipDuplicateIdPost(
		{ id }
	);
};

export interface ProfessorInternshipProps {
	professorId?: string;
	skills?: Array<string>;
	categories?: Array<string>;
	Type?: Array<string>;
	Status?: Array<string>;
	Applicability?: Array<boolean>;
	ValidationState?: Array<boolean>;
	UniversityYear?: Array<string>;
	Faculty?: Array<string>;
	Location?: Array<string>;
	AvailablePositions?: Array<string>;
	searchTerm?: string;
	page?: number;
	pageSize?: number;
	sortKey?: InternshipSortFieldEnum;
	sort?: SortDirectionEnum;
}

export const getAllProfessorInternshipV2 = (
	props: ProfessorInternshipProps
) => {
	const {
		professorId,
		skills,
		categories,
		Type: types,
		Status: statuses,
		Applicability: applicabilityTypes,
		ValidationState: validationStates,
		UniversityYear: universityYears,
		AvailablePositions: availablePositions,
		searchTerm,
		page,
		pageSize,
		sortKey: sortField,
		sort: sortDirection,
	} = props;

	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetAllGet({
		professorId,
		skills: arrayParamAjust(skills),
		categories: arrayParamAjust(categories),
		types: arrayParamAjust(
			types?.map(
				(e) =>
					InternshipEngagementType[e as keyof typeof InternshipEngagementType]
			)
		),
		statuses: arrayParamAjust(
			statuses?.map((e) => InternshipStatus[e as keyof typeof InternshipStatus])
		),
		applicabilityTypes: arrayParamAjust(applicabilityTypes),
		validationStates: arrayParamAjust(validationStates),
		universityYears: arrayParamAjust(
			universityYears?.map((y) => Number(y.split("-")[0]))
		),
		availablePositions,
		searchTerm,
		page,
		pageSize,
		sortField,
		sortDirection,
	});
};

export const getAllProfessorInternshipsAdmin = (
	props: ProfessorInternshipProps
) => {
	const {
		skills,
		categories,
		Type: types,
		Status: statuses,
		Applicability: applicabilityTypes,
		ValidationState: validationStates,
		UniversityYear: universityYears,
		Faculty: faculties,
		searchTerm,
		page,
		pageSize,
		sortKey: sortField,
		sort: sortDirection,
	} = props;

	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetAllAdminGet(
		{
			skills: arrayParamAjust(skills),
			categories: arrayParamAjust(categories),
			types: arrayParamAjust(
				types?.map(
					(e) =>
						InternshipEngagementType[e as keyof typeof InternshipEngagementType]
				)
			),
			statuses: arrayParamAjust(
				statuses?.map(
					(e) => InternshipStatus[e as keyof typeof InternshipStatus]
				)
			),
			applicabilityTypes: arrayParamAjust(applicabilityTypes),
			validationStates: arrayParamAjust(validationStates),
			universityYears: arrayParamAjust(
				universityYears?.map((y) => Number(y.split("-")[0]))
			),
			faculties,
			searchTerm,
			page,
			pageSize,
			sortField,
			sortDirection,
		}
	);
};

export interface ProfessorInternshipsForFacultyProps {
	skills?: Array<string>;
	categories?: Array<string>;
	Type?: Array<string>;
	Status?: Array<string>;
	ValidationState?: Array<boolean>;
	UniversityYear?: Array<string>;
	searchTerm?: string;
	page?: number;
	pageSize?: number;
	sortKey?: InternshipSortFieldEnum;
	sort?: SortDirectionEnum;
}

export const getAllProfessorInternshipsForFacultyV2 = (
	props: ProfessorInternshipsForFacultyProps
) => {
	const {
		skills,
		categories,
		Type: types,
		Status: statuses,
		ValidationState: validationStates,
		UniversityYear: universityYears,
		searchTerm,
		page,
		pageSize,
		sortKey: sortField,
		sort: sortDirection,
	} = props;

	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetAllForFacultyGet(
		{
			skills: arrayParamAjust(skills),
			categories: arrayParamAjust(categories),
			types: arrayParamAjust(
				types?.map(
					(e) =>
						InternshipEngagementType[e as keyof typeof InternshipEngagementType]
				)
			),
			statuses: arrayParamAjust(
				statuses?.map(
					(e) => InternshipStatus[e as keyof typeof InternshipStatus]
				)
			),
			validationStates: arrayParamAjust(validationStates),
			universityYears: arrayParamAjust(
				universityYears?.map((y) => Number(y.split("-")[0]))
			),
			searchTerm,
			page,
			pageSize,
			sortField,
			sortDirection,
		}
	);
};

export interface ProfessorInternshipsForDepartmentProps {
	skills?: Array<string>;
	categories?: Array<string>;
	Type?: Array<string>;
	Status?: Array<string>;
	ValidationState?: Array<boolean>;
	UniversityYear?: Array<string>;
	searchTerm?: string;
	page?: number;
	pageSize?: number;
	sortKey?: InternshipSortFieldEnum;
	sort?: SortDirectionEnum;
}

export const getAllProfessorInternshipsForDepartmentV2 = (
	props: ProfessorInternshipsForDepartmentProps
) => {
	const {
		skills,
		categories,
		Type: types,
		Status: statuses,
		ValidationState: validationStates,
		UniversityYear: universityYears,
		searchTerm,
		page,
		pageSize,
		sortKey: sortField,
		sort: sortDirection,
	} = props;

	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetAllForDepartmentGet(
		{
			skills: arrayParamAjust(skills),
			categories: arrayParamAjust(categories),
			types: arrayParamAjust(
				types?.map(
					(e) =>
						InternshipEngagementType[e as keyof typeof InternshipEngagementType]
				)
			),
			statuses: arrayParamAjust(
				statuses?.map(
					(e) => InternshipStatus[e as keyof typeof InternshipStatus]
				)
			),
			validationStates: arrayParamAjust(validationStates),
			universityYears: arrayParamAjust(
				universityYears?.map((y) => Number(y.split("-")[0]))
			),
			searchTerm,
			page,
			pageSize,
			sortField,
			sortDirection,
		}
	);
};

export interface ProfessorInternshipsForStudyProgramProps {
	skills?: Array<string>;
	categories?: Array<string>;
	Type?: Array<string>;
	Status?: Array<string>;
	ValidationState?: Array<boolean>;
	UniversityYear?: Array<string>;
	searchTerm?: string;
	page?: number;
	pageSize?: number;
	sortKey?: InternshipSortFieldEnum;
	sort?: SortDirectionEnum;
}

export const getAllProfessorInternshipsForStudyProgramV2 = (
	props: ProfessorInternshipsForStudyProgramProps
) => {
	const {
		skills,
		categories,
		Type: types,
		Status: statuses,
		ValidationState: validationStates,
		UniversityYear: universityYears,
		searchTerm,
		page,
		pageSize,
		sortKey: sortField,
		sort: sortDirection,
	} = props;

	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetAllForStudyProgramGet(
		{
			skills: arrayParamAjust(skills),
			categories: arrayParamAjust(categories),
			types: arrayParamAjust(
				types?.map(
					(e) =>
						InternshipEngagementType[e as keyof typeof InternshipEngagementType]
				)
			),
			statuses: arrayParamAjust(
				statuses?.map(
					(e) => InternshipStatus[e as keyof typeof InternshipStatus]
				)
			),
			validationStates: arrayParamAjust(validationStates),
			universityYears: arrayParamAjust(
				universityYears?.map((y) => Number(y.split("-")[0]))
			),
			searchTerm,
			page,
			pageSize,
			sortField,
			sortDirection,
		}
	);
};

export const getAllOpportunitiesV2 = async (
	props: ProfessorInternshipProps
) => {
	const {
		skills,
		categories,
		Status: statuses,
		ValidationState: validationStates,
		Location: locations,
		AvailablePositions: availablePositions,
		searchTerm,
		page,
		pageSize,
		sortKey: sortField,
		sort: sortDirection,
	} = props;

	const queryParams: Record<string, ProfessorInternshipQueryParamValue> = {
		Page: page != null ? Number(page) : 1,
		PageSize: pageSize != null ? Number(pageSize) : 12,
		SortField: sortField ?? InternshipSortFieldEnum.PublishDate,
		SortDirection: sortDirection ?? SortDirectionEnum.Descending,
	};

	if (searchTerm) {
		queryParams.SearchTerm = searchTerm;
	}
	if (arrayParamAjust(skills)) {
		queryParams.Skills = arrayParamAjust(skills)!;
	}
	if (arrayParamAjust(categories)) {
		queryParams.Categories = arrayParamAjust(categories)!;
	}
	const mappedStatuses = statuses?.map(
		(e) => InternshipStatus[e as keyof typeof InternshipStatus]
	);
	if (arrayParamAjust(mappedStatuses)) {
		queryParams.Statuses = arrayParamAjust(mappedStatuses)!;
	}
	if (arrayParamAjust(validationStates)) {
		queryParams.ValidationStates = arrayParamAjust(validationStates)!;
	}
	if (arrayParamAjust(locations)) {
		queryParams.Locations = arrayParamAjust(locations)!;
	}
	if (arrayParamAjust(availablePositions)) {
		queryParams.AvailablePositions = arrayParamAjust(availablePositions)!;
	}

	const json = await professorInternshipGet("GetOpportunities", queryParams);

	return InternshipDTOPagedResponseFromJSON(json);
};

export const getOpportunitiesFilters = async () => {
	return professorInternshipGet("GetOpportunitiesFilters", {});
};

export const getAllProfessorInternshipFilters = () => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetInternshipFiltersGet();
};

export const getFacultyProfessorInternshipFilters = () => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetFacultyInternshipFiltersGet();
};

export const getDepartmentProfessorInternshipFilters = () => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetDepartmentInternshipFiltersGet();
};

export const getStudyProgramProfessorInternshipFilters = () => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetStudyProgramInternshipFiltersGet();
};

export const getProfessorInternshipById = (id: string) => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipGetByIdIdGet(
		{ id }
	);
};

export const updateProfessorInternshipViewCountByInternshipId = async (
	id: string
) => {
	await AuthProfessorInternshipApiFactory().apiProfessorInternshipUpdateViewCountByInternshipIdIdPut(
		{ id }
	);
};

export const updateProfessorInternshipInfo = async (
	id: string,
	professorInternshipAddUpdateDTO?: ProfessorInternshipAddUpdateDTO
) => {
	await AuthProfessorInternshipApiFactory().apiProfessorInternshipUpdateIdPut({
		id,
		professorInternshipAddUpdateDTO,
	});
};

export const updateValidation = (internshipId: string, status: boolean) => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipChangeValidStatusPost(
		{ internshipId, status }
	);
};

export const changeStudyPrograms = (id: string, requestBody: Array<string>) => {
	return AuthProfessorInternshipApiFactory().apiProfessorInternshipChangeStudyProgramIdPut(
		{ id, requestBody }
	);
};
